import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Button, Typography, Tag, message } from "antd";
import { PlusOutlined, EditOutlined, HolderOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";
import { useState, useEffect, useCallback, useRef } from "react";

const { Title, Text } = Typography;

function FeaturesList() {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  // State management
  const [loading, setLoading] = useState(false);
  const [features, setFeatures] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} features`,
  });

  // Filters
  const [filters] = useState({
    searchText: "",
    status: "all",
  });

  const getFeatures = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching features with params:", requestParams);
        const response = await masterService.getFeatureList(requestParams);
        console.log("Countries API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const featuresData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Features data:", featuresData);
          console.log("Pagination data:", paginationData);

          // Ensure countriesData is an array
          if (Array.isArray(featuresData)) {
            setFeatures(featuresData);
          } else {
            console.error("Features data is not an array:", featuresData);
            setFeatures([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch countries" });
          setFeatures([]);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
        notify.error({ message: "Failed to fetch countries" });
        setFeatures([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText, filters.status]
  );

  // Load countries data
  useEffect(() => {
    setPageTitle("Features");
    getFeatures();
  }, [getFeatures, setPageTitle]);

  // Table removed; no table change handler needed

  // Handle add country
  const handleAddFeature = () => {
    navigate("/features/add/new");
  };

  // Handle edit country
  const handleEditFeature = (featureId) => {
    navigate(`/features/edit/${featureId}`);
  };

  // Drag & drop reordering (Braille-style handle)
  const [dragIndex, setDragIndex] = useState(null);
  const cardRefs = useRef([]);

  const onDragStart = (index, e) => {
    setDragIndex(index);
    // carry index as text
    e.dataTransfer.setData("text/plain", String(index));
    // effect
    e.dataTransfer.effectAllowed = "move";

    // Set custom drag image to show the whole card
    const cardElement = cardRefs.current[index];
    if (cardElement) {
      // Clone the card for drag preview
      const clone = cardElement.cloneNode(true);
      clone.style.position = "absolute";
      clone.style.top = "-9999px";
      clone.style.width = cardElement.offsetWidth + "px";
      clone.style.opacity = "0.8";
      document.body.appendChild(clone);
      e.dataTransfer.setDragImage(
        clone,
        e.clientX - cardElement.getBoundingClientRect().left,
        e.clientY - cardElement.getBoundingClientRect().top
      );
      // Clean up clone after drag starts
      setTimeout(() => document.body.removeChild(clone), 0);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onDrop = (hoverIndex, e) => {
    e.preventDefault();
    const fromIndex =
      dragIndex ?? parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (Number.isNaN(fromIndex) || fromIndex === hoverIndex) return;
    setFeatures((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(hoverIndex, 0, moved);
      // Renumber order_number locally (1-based)
      return next.map((it, idx) => ({ ...it, order_number: idx + 1 }));
    });
    setDragIndex(null);
  };

  const handleSaveOrder = async () => {
    try {
      if (!Array.isArray(features) || features.length === 0) return;
      setLoading(true);
      // Build bulk payload with only id, title, order_number
      const items = features.map((item, idx) => ({
        id: item.feature_id ?? item.id,
        title: item.title,
        order_number: idx + 1,
      }));
      await masterService.reorderFeatures({ items });
      message.success("Order saved");
      // Refresh from server to ensure consistency
      await getFeatures();
    } catch (err) {
      console.error("Save order error", err);
      message.error("Failed to save order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Features ({pagination.total})
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 300,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddFeature}
            size="middle"
          >
            Add Feature
          </Button>
          <Button
            type="primary"
            onClick={handleSaveOrder}
            loading={loading}
            disabled={loading}
            size="middle"
          >
            Save Order
          </Button>
        </div>
      </div>

      {loading ? (
        <Text type="secondary">Loading...</Text>
      ) : features.length === 0 ? (
        <Text type="secondary">No features found</Text>
      ) : (
        <div className="space-y-2">
          {features.map((item, index) => (
            <Card

              key={item.feature_id || item.id}
              size="small"
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(index, e)}
              ref={(el) => (cardRefs.current[index] = el)}
              draggable
              onDragStart={(e) => onDragStart(index, e)}
              style={{ cursor: "grab" }}
            >
              <div className="grid grid-cols-3 gap-2">
                <div className="flex items-center gap-2">
                  <span title="Drag to reorder" style={{ color: "#999" }}>
                    <HolderOutlined />
                  </span>
                  {item?.order_number !== undefined && (
                    <Tag color="green">{item.order_number}</Tag>
                  )}
                  <div>
                    <Text strong style={{ fontSize: 14 }}>
                      {item.title}
                    </Text>
                  </div>
                </div>
                {item?.answer_type && (
                  <div>
                    <Text type="secondary">{item.answer_type}</Text>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Tag color={item.is_active ? "green" : "red"}>
                    {item.is_active ? "Active" : "Inactive"}
                  </Tag>
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() =>
                      handleEditFeature(item.feature_id ?? item.id)
                    }
                    size="small"
                  >
                    Edit
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Card>
  );
}

export default FeaturesList;
